// Outbound-link handling specifically for in-app browser environments
// (Facebook, Instagram, Messenger). FB's in-app browser intercepts most
// outbound nav and blocks app handoffs to Amazon / Play Books — so a
// straight `<a href="https://amazon.com/...">` quietly does nothing on
// click. The user taps, the event fires, and they sit there confused.
//
// Workaround: on Android-in-IAB, rewrite the destination to an
// `intent://` URL with a https fallback. The OS then either opens the
// native retailer app (if installed) or hands off to Chrome — both of
// which leave FB's in-app shell, which is what we want.
//
// On iOS the situation is messier (Universal Links sometimes work,
// `target="_blank"` sometimes doesn't), but Android is the highest-
// volume case and the fix is unambiguous, so start here.

const FB_IAB_RE = /FB_IAB|FBAN|FBAV|FBIOS|Messenger/i;
const INSTAGRAM_RE = /Instagram/i;
const ANDROID_RE = /Android/i;
const IOS_RE = /iPhone|iPad|iPod/i;

// Per-region package names. Amazon India ships its own APK
// (`in.amazon.mShop.android.shopping`); other regions use the global one.
const AMAZON_PACKAGES = {
  amazon_us: 'com.amazon.mShop.android.shopping',
  amazon_uk: 'com.amazon.mShop.android.shopping',
  amazon_in: 'in.amazon.mShop.android.shopping',
  amazon_ca: 'com.amazon.mShop.android.shopping',
  amazon_au: 'com.amazon.mShop.android.shopping',
  amazon_jp: 'com.amazon.mShop.android.shopping',
};

const PLAY_BOOKS_PACKAGE = 'com.google.android.apps.books';

export function getUA() {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
}

export function isAndroid() {
  return ANDROID_RE.test(getUA());
}

export function isIOS() {
  return IOS_RE.test(getUA());
}

export function isInAppBrowser() {
  const ua = getUA();
  return FB_IAB_RE.test(ua) || INSTAGRAM_RE.test(ua);
}

export function platform() {
  if (isAndroid()) return 'android';
  if (isIOS()) return 'ios';
  return 'other';
}

// Convert an https URL to an Android `intent://` URL with a fallback so
// it opens the native app if installed or hands off to Chrome otherwise.
// Returns the original URL unchanged if `store` isn't one we have a
// package mapping for.
export function buildAndroidIntentUrl(url, store) {
  try {
    const u = new URL(url);
    let pkg = null;
    if (store && AMAZON_PACKAGES[store]) {
      pkg = AMAZON_PACKAGES[store];
    } else if (store === 'google_play') {
      pkg = PLAY_BOOKS_PACKAGE;
    }
    if (!pkg) return url;
    const fallback = encodeURIComponent(url);
    const scheme = u.protocol.replace(':', ''); // 'https'
    return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${scheme};package=${pkg};S.browser_fallback_url=${fallback};end`;
  } catch {
    return url;
  }
}

// True iff we're in a context where rewriting the URL helps: Android +
// in-app browser + a store we have a package for. Caller checks this
// before calling preventDefault on the click.
//
// Escape hatch: `?nofix=1` on the page URL disables the rewrite so we
// can replay the pre-fix broken behavior for diagnostics.
export function shouldRewriteOutbound(store) {
  if (typeof window !== 'undefined' && /[?&]nofix=1\b/.test(window.location.search)) {
    return false;
  }
  if (!isAndroid()) return false;
  if (!isInAppBrowser()) return false;
  return Boolean(AMAZON_PACKAGES[store] || store === 'google_play');
}
